import * as React from 'react';

import type { GovernmentReportingServiceType } from '@rematter/frontend-lib-api-client';
import {
  Button,
  Col,
  ConsoleDialog,
  Row,
  Select,
  Switch,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue } from 'jotai';
import { LucideTag } from 'lucide-react';

import * as itemCodesState from '../../_state/itemJurisdictionCodes.state';
import { useTranslations } from '../../i18n/useTranslations';
import { FormSection } from '../_shared/FormSection';

import { getServiceTypeOptions } from './serviceTypeOptions';

import { SelectMaterial } from '~/shared/components/select-fields/SelectMaterial';

export const ItemJurisdictionCodeFormDialog = () => {
  const t = useTranslations();
  const [itemId, setItemId] = React.useState<string | undefined>(undefined);
  const [serviceType, setServiceType] = React.useState('');
  const [code, setCode] = React.useState('');
  const [isReportable, setIsReportable] = React.useState(true);
  const editingCodeId = useAtomValue(itemCodesState.$editingItemCode);
  const codes = useAtomValue(itemCodesState.$rawItemJurisdictionCodes);
  const [isOpen, setIsOpen] = useAtom(itemCodesState.$isItemCodeDialogOpen);
  const [{ isPending: isCreating }, createCode] = useAtom(
    itemCodesState.$createItemJurisdictionCode,
  );
  const [{ isPending: isUpdating }, updateCode] = useAtom(
    itemCodesState.$updateItemJurisdictionCode,
  );
  const toast = useToast();

  const editingCode = React.useMemo(
    () => codes.data?.find((c) => c.id === editingCodeId),
    [codes.data, editingCodeId],
  );
  const isEditing = !!editingCode;
  const isPending = isCreating || isUpdating;

  const serviceTypeOptions = React.useMemo(
    () => getServiceTypeOptions(t.complianceView.serviceTypeLabels),
    [t],
  );

  React.useEffect(() => {
    if (isOpen && editingCode) {
      setItemId(editingCode.itemId);
      setServiceType(editingCode.serviceType);
      setCode(editingCode.code);
      setIsReportable(editingCode.isReportable);
    } else if (isOpen) {
      setItemId(undefined);
      setServiceType('');
      setCode('');
      setIsReportable(true);
    }
  }, [isOpen, editingCode]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSubmit = async () => {
    try {
      if (isEditing && editingCode) {
        await updateCode({
          id: editingCode.id,
          serviceType: (serviceType || undefined) as
            | GovernmentReportingServiceType
            | undefined,
          code: code || undefined,
          isReportable,
        });
        toast.success(t.complianceView.itemCodes.dialog.messages.updateSuccess);
      } else {
        if (!itemId || !serviceType || !code) return;
        await createCode({
          itemId,
          serviceType: serviceType as GovernmentReportingServiceType,
          code,
          isReportable,
        });
        toast.success(t.complianceView.itemCodes.dialog.messages.createSuccess);
      }
      handleClose();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      if (isEditing) {
        toast.error(
          t.complianceView.itemCodes.dialog.messages.updateError(msg),
        );
      } else {
        toast.error(
          t.complianceView.itemCodes.dialog.messages.createError(msg),
        );
      }
    }
  };

  const canSubmit = isEditing ? !!code : !!itemId && !!serviceType && !!code;

  return (
    <ConsoleDialog
      className="@ItemJurisdictionCodeFormDialog"
      title={
        isEditing
          ? t.complianceView.itemCodes.dialog.editTitle
          : t.complianceView.itemCodes.dialog.createTitle
      }
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      isLoading={isPending}
      askBeforeClosing={!!itemId || code !== ''}
      content={
        <Col gap6>
          <FormSection
            icon={LucideTag}
            title={t.complianceView.itemCodes.dialog.sections.jurisdictionCode}
          >
            <Col gap4>
              {!isEditing && (
                <SelectMaterial
                  label={t.complianceView.itemCodes.dialog.fields.item}
                  value={itemId}
                  onChange={(value) => setItemId(value ?? undefined)}
                  placeholder={
                    t.complianceView.itemCodes.dialog.placeholders.item
                  }
                  isRequired
                />
              )}
              <Select
                label={t.complianceView.itemCodes.dialog.fields.serviceType}
                value={serviceType}
                onChange={setServiceType}
                options={serviceTypeOptions}
                placeholder={
                  t.complianceView.itemCodes.dialog.placeholders.serviceType
                }
                isRequired
              />
              <TextField
                label={t.complianceView.itemCodes.dialog.fields.code}
                value={code}
                onChange={setCode}
                placeholder={
                  t.complianceView.itemCodes.dialog.placeholders.code
                }
                isRequired
              />
              <Row className="items-center gap-3">
                <Switch isSelected={isReportable} onChange={setIsReportable} />
                <span className="text-sm">
                  {t.complianceView.itemCodes.dialog.fields.isReportable}
                </span>
              </Row>
            </Col>
          </FormSection>
        </Col>
      }
      footer={
        <Button
          color="primary"
          variant="solid"
          isDisabled={!canSubmit}
          isPending={isPending}
          isLoading={isPending}
          onPress={handleSubmit}
        >
          {isEditing
            ? t.complianceView.itemCodes.dialog.actions.save
            : t.complianceView.itemCodes.dialog.actions.create}
        </Button>
      }
    />
  );
};
